import { lastValueFrom } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { isNil } from 'lodash-es';
import {
  ClientObservabilityJobOutcome,
  IClientObservabilityIngestion,
  IClientObservabilityJobsPage,
  IClientObservabilityPipeline
} from '../models/IClientObservability';
import { IAISummaryResponse } from '../models/IAISummary';
import {
  IClientObservabilityApiOperation,
  ObservabilityFlagFamily
} from '../models/IClientObservabilityApiOperation';
import {
  IGeoAlternateRead,
  IGeoAlternateRequest,
  IGeoMonitoring,
  IGeoMonitoringDetail
} from '../models/IGeoMonitoring';
import { IGeoObservability } from '../models/IGeoObservability';
import {
  IObservabilitySignalRollupDetailPage,
  IObservabilitySignalRollupDetailQuery,
  IObservabilitySignalRollupPage,
  IObservabilitySignalRollupQuery,
  IObservabilitySignals
} from '../models/IObservabilitySignals';

export class ClientObservabilityApis
  implements IClientObservabilityApiOperation
{
  private constructor(
    private readonly httpClient: HttpClient,
    private readonly apiV1: string
  ) {}

  public static clientObservabilityApiOperation(
    httpClient: HttpClient,
    apiV1: string
  ): IClientObservabilityApiOperation {
    return new ClientObservabilityApis(httpClient, apiV1);
  }

  public getGeoObservability(clientId: number): Promise<IGeoObservability> {
    return this.get<IGeoObservability>('/bff/observability/geo', { clientId });
  }

  public getGeoMonitoring(): Promise<IGeoMonitoring> {
    return this.get<IGeoMonitoring>('/bff/geo-monitoring', {});
  }

  public getGeoMonitoringStudy(studyId: number): Promise<IGeoMonitoringDetail> {
    return this.get<IGeoMonitoringDetail>(
      `/bff/geo-monitoring/studies/${studyId}`,
      {}
    );
  }

  public analyzeGeoPtw(
    request: IGeoAlternateRequest
  ): Promise<IGeoAlternateRead> {
    return this.post<IGeoAlternateRead>(
      '/bff/geo/alternate-reads/ptw/analyze',
      request
    );
  }

  public analyzeGeoWeekRemoval(
    request: IGeoAlternateRequest
  ): Promise<IGeoAlternateRead> {
    return this.post<IGeoAlternateRead>(
      '/bff/geo/alternate-reads/week-removal/analyze',
      request
    );
  }

  public getObservabilitySignals(
    clientId: number,
    flagFamily: ObservabilityFlagFamily,
    summaryOnly = false,
    includeDrivers = false,
    dateStart?: string,
    dateStop?: string
  ): Promise<IObservabilitySignals> {
    return this.get<IObservabilitySignals>('/bff/observability/signals', {
      clientId,
      flagFamily,
      summaryOnly,
      includeDrivers,
      dateStart,
      dateStop
    });
  }

  public getSignalRollups(
    query: IObservabilitySignalRollupQuery
  ): Promise<IObservabilitySignalRollupPage> {
    return this.get<IObservabilitySignalRollupPage>(
      '/bff/observability/signal-rollups',
      { ...query }
    );
  }

  public getSignalRollupDetails(
    query: IObservabilitySignalRollupDetailQuery
  ): Promise<IObservabilitySignalRollupDetailPage> {
    return this.get<IObservabilitySignalRollupDetailPage>(
      '/bff/observability/signal-rollup-details',
      { ...query }
    );
  }

  public getIngestionHealth(
    clientId: number,
    lookbackDays?: number
  ): Promise<IClientObservabilityIngestion> {
    return this.get<IClientObservabilityIngestion>(
      '/bff/client-observability/ingestion',
      {
        clientId,
        lookbackDays
      }
    );
  }

  public getPipelineHealth(
    clientId: number,
    lookbackDays?: number
  ): Promise<IClientObservabilityPipeline> {
    return this.get<IClientObservabilityPipeline>(
      '/bff/client-observability/pipeline',
      {
        clientId,
        lookbackDays
      }
    );
  }

  public getJobs(
    clientId: number,
    outcome: ClientObservabilityJobOutcome,
    lookbackDays?: number,
    page = 1,
    pageSize = 25
  ): Promise<IClientObservabilityJobsPage> {
    return this.get<IClientObservabilityJobsPage>(
      '/bff/client-observability/jobs',
      {
        clientId,
        outcome,
        lookbackDays,
        page,
        pageSize
      }
    );
  }

  public getSpecSummary(
    clientId: number,
    specType: 'ccmaf' | 'transaction',
    specId: number
  ): Promise<IAISummaryResponse> {
    return this.get<IAISummaryResponse>(
      '/bff/client-observability/spec-summary',
      {
        clientId,
        specType,
        specId
      }
    );
  }

  private post<T>(path: string, body: IGeoAlternateRequest): Promise<T> {
    return lastValueFrom(this.httpClient.post<T>(`${this.apiV1}${path}`, body));
  }

  private get<T>(
    path: string,
    queryParameters: Record<
      string,
      string | number | boolean | string[] | undefined
    >
  ): Promise<T> {
    const url = new URL(`${this.apiV1}${path}`);
    Object.entries(queryParameters).forEach(([key, value]) =>
      [value]
        .flat()
        .filter((item) => !isNil(item))
        .forEach((item) => url.searchParams.append(key, String(item)))
    );
    return lastValueFrom(this.httpClient.get<T>(url.toString()));
  }
}
